import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/auth.js'
import { api } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'
import { purchasesAllowed } from '../utils/platform.js'

const TIER_LABELS = { free: 'Free', plus: 'Plus', researcher: 'Researcher', family: 'Researcher', archive: 'Researcher' }

function formatPrice({ amount, currency, interval }) {
  const n = (amount / 100).toLocaleString('nl-NL', {
    style: 'currency', currency: (currency || 'eur').toUpperCase(),
    minimumFractionDigits: 2,
  })
  if (!interval) return n
  return `${n} / ${interval === 'year' ? 'year' : 'month'}`
}

function annualDiscountPct(monthly, annual) {
  if (!monthly?.amount || !annual?.amount) return null
  const full = monthly.amount * 12
  if (annual.amount >= full) return null
  return Math.round(((full - annual.amount) / full) * 100)
}

export default function BillingPage() {
  usePageTitle('Billing')
  const { user, refresh } = useAuthStore()
  const [catalogue, setCatalogue] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [reports, setReports] = useState([])
  const [storyCredits, setStoryCredits] = useState({ remaining: 0, monthly_limit: 0 })
  const [interval, setInterval] = useState('month') // 'month' | 'year'
  const [busy, setBusy] = useState(null)
  const [err, setErr] = useState(null)
  const [params] = useSearchParams()

  useEffect(() => {
    api.get('/billing/catalogue').then(setCatalogue).catch(e => setErr(e.message))
    api.get('/billing/subscription').then(setSubscription).catch(() => {})
    api.get('/billing/story-reports').then(setReports).catch(() => setReports([]))
    api.get('/billing/story-credits').then(setStoryCredits).catch(() => {})
  }, [])

  // Returning from a successful checkout — re-sync user so the new tier
  // lands without a logout. Webhook may race the redirect; retry briefly.
  useEffect(() => {
    if (params.get('checkout') !== 'success') return
    let cancelled = false
    async function poll() {
      for (let i = 0; i < 6; i++) {
        await new Promise(r => setTimeout(r, 1000))
        if (cancelled) return
        await refresh()
        const s = await api.get('/billing/subscription').catch(() => null)
        if (s) setSubscription(s)
        const r = await api.get('/billing/story-reports').catch(() => null)
        if (r) setReports(r)
        const c = await api.get('/billing/story-credits').catch(() => null)
        if (c) setStoryCredits(c)
      }
    }
    poll()
    return () => { cancelled = true }
  }, [params, refresh])

  async function startCheckout(priceId, mode) {
    setBusy(priceId); setErr(null)
    try {
      const { url } = await api.post('/billing/checkout', { priceId, mode })
      window.location.href = url
    } catch (e) {
      setErr(e.message); setBusy(null)
    }
  }

  async function openPortal() {
    setBusy('portal'); setErr(null)
    try {
      const { url } = await api.post('/billing/portal', {})
      window.location.href = url
    } catch (e) {
      setErr(e.message); setBusy(null)
    }
  }

  async function downloadStoryReport(orderId) {
    try {
      const { url } = await api.get(`/billing/story-reports/${orderId}/download`)
      window.open(url, '_blank')
    } catch (e) {
      setErr(e.message)
    }
  }

  // ?plan= highlights the card the user came here to choose.
  const highlightPlan = params.get('plan')
  const plans = catalogue?.plans || []
  const report = catalogue?.storyReport
  const enabled = catalogue?.enabled
  const currentPriceId = subscription?.priceId

  const checkoutCancelled = params.get('checkout') === 'cancel'
  const checkoutSuccess   = params.get('checkout') === 'success'
  const creditRedeemed    = params.get('credit') === 'used'

  // Native (Apple 3.1.1 / Google Play): no in-app purchase or external-purchase
  // link for digital goods. Show the current plan + a plain note pointing to the
  // web; all checkout / plan-selection / Story-Report purchase UI is hidden.
  if (!purchasesAllowed()) {
    return (
      <div className="billing-page">
        <div className="page-header">
          <div className="page-header__top">
            <div>
              <div className="page-header__label">Account</div>
              <h1 className="page-header__title">Plan</h1>
            </div>
          </div>
        </div>
        <div className="billing-page__body">
          <p style={{ fontSize: '1rem', marginBottom: '.75rem' }}>
            Your current plan: <strong>{TIER_LABELS[user?.tier] || 'Free'}</strong>
          </p>
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Subscriptions, Story Reports and plan changes are managed on the web
            at <strong>genealogic.ai</strong>. Your plan applies here automatically.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="billing-page">
      <div className="page-header">
        <div className="page-header__top">
          <div>
            <div className="page-header__label">Account</div>
            <h1 className="page-header__title">Billing</h1>
          </div>
        </div>
      </div>
      <div className="billing-page__body">

      {checkoutSuccess && (
        <div className="form-success" style={{ marginBottom: '1rem' }}>
          Payment complete. Your plan is updating — this may take a moment.
        </div>
      )}
      {checkoutCancelled && (
        <div style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>
          Checkout cancelled. No charge was made.
        </div>
      )}
      {creditRedeemed && (
        <div className="form-success" style={{ marginBottom: '1rem' }}>
          Your included Story Report credit has been redeemed — the report is being prepared and will appear below in a moment.
        </div>
      )}
      {err && <p className="error" style={{ marginBottom: '1rem' }}>{err}</p>}

      {/* Current subscription summary */}
      <div className="profile-card" style={{ marginBottom: '1.5rem' }}>
        <div className="profile-card__header">
          <div style={{ flex: 1 }}>
            <div className="profile-card__name">
              {TIER_LABELS[user?.tier] || 'Free'} plan
            </div>
            <div className="profile-card__email">
              {subscription?.status
                ? <>Status: <strong>{subscription.status}</strong>
                    {subscription.currentPeriodEnd && (
                      <> · renews {new Date(subscription.currentPeriodEnd).toLocaleDateString()}</>
                    )}
                    {subscription.cancelAtPeriodEnd && <> · cancels at period end</>}
                  </>
                : 'No active subscription.'}
            </div>
          </div>
          {subscription?.hasStripeCustomer && (
            <button
              className="btn btn-sm"
              disabled={busy === 'portal'}
              onClick={openPortal}
              style={{
                background: 'transparent',
                color: 'var(--green)',
                border: '1px solid var(--green)',
                fontWeight: 500,
              }}
            >
              {busy === 'portal' ? 'Opening…' : 'Manage subscription'}
            </button>
          )}
        </div>
      </div>

      {!enabled && (
        <div style={{
          padding: '1rem', border: '1px dashed var(--border)', borderRadius: 8,
          color: 'var(--text-muted)', marginBottom: '1rem',
        }}>
          Stripe billing is not configured on this environment.
        </div>
      )}

      {/* Interval switcher */}
      <div className="interval-switcher">
        <button
          className={`interval-switcher__btn ${interval === 'month' ? 'is-active' : ''}`}
          onClick={() => setInterval('month')}
        >Monthly</button>
        <button
          className={`interval-switcher__btn ${interval === 'year' ? 'is-active' : ''}`}
          onClick={() => setInterval('year')}
        >Annual</button>
        <span className="interval-switcher__hint">
          Annual plans typically come with a discount.
        </span>
      </div>

      {/* Plan cards */}
      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {plans.map(plan => {
          const priceSide = interval === 'year' ? plan.annual : plan.monthly
          const active = currentPriceId && currentPriceId === priceSide?.priceId
          const discount = interval === 'year' ? annualDiscountPct(plan.monthly, plan.annual) : null
          const highlighted = highlightPlan && highlightPlan === plan.id && !active
          return (
            <div
              key={plan.id}
              className="profile-card"
              style={{
                display: 'flex', flexDirection: 'column',
                outline: highlighted ? '2px solid var(--gold, #c9a961)' : 'none',
                outlineOffset: highlighted ? '2px' : 0,
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{plan.name}</div>
              <div style={{ color: 'var(--text-muted)', marginBottom: '0.6rem', fontSize: '0.9rem' }}>
                {plan.description}
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 600 }}>
                {priceSide ? formatPrice(priceSide) : '—'}
              </div>
              {discount && (
                <div style={{ color: 'var(--green)', fontSize: '0.85rem', fontWeight: 500 }}>
                  Save {discount}% vs monthly
                </div>
              )}
              <ul style={{ margin: '0.75rem 0 1rem', paddingLeft: '1.1rem', color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.6 }}>
                {plan.features.map(f => <li key={f}>{f}</li>)}
              </ul>
              <button
                className="btn btn-primary"
                disabled={!enabled || !priceSide?.priceId || busy === priceSide?.priceId || active}
                onClick={() => startCheckout(priceSide.priceId, 'subscription')}
                style={{ marginTop: 'auto' }}
              >
                {active
                  ? 'Current plan'
                  : busy === priceSide?.priceId
                    ? 'Redirecting…'
                    : `Choose ${plan.name}`}
              </button>
            </div>
          )
        })}
      </div>

      {/* Story Report one-off */}
      {report && (
        <div className="profile-section" style={{ marginTop: '2rem' }}>
          <h2 className="profile-section__title">Story Report — one-off</h2>
          {storyCredits.monthly_limit > 0 && (
            <div style={{
              padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: '0.75rem',
              background: 'color-mix(in srgb, var(--gold, #c9a961) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--gold, #c9a961) 30%, transparent)',
              fontSize: '0.88rem',
            }}>
              {storyCredits.remaining > 0
                ? <>You have <strong>{storyCredits.remaining}</strong> included Story Report{storyCredits.remaining === 1 ? '' : 's'} this month as part of your Researcher plan. Order from any person's profile to redeem without being charged.</>
                : <>You've used your included Story Report for this month. Additional reports are {formatPrice(report)}.</>}
            </div>
          )}
          <div className="profile-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 600 }}>{report.name}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{report.description}</div>
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 600 }}>{formatPrice(report)}</div>
            <button
              className="btn btn-primary btn-sm"
              disabled={!enabled || !report.priceId || busy === report.priceId}
              onClick={() => startCheckout(report.priceId, 'payment')}
            >
              {busy === report.priceId ? 'Redirecting…' : 'Buy a Report'}
            </button>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: '0.6rem' }}>
            You can also order a Story Report from any person's profile to pre-select the ancestor.
          </p>

          {reports.length > 0 && (
            <>
              <h3 style={{ marginTop: '1.5rem', fontSize: '0.95rem' }}>Your orders</h3>
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                {reports.map(r => (
                  <div key={r.id} style={{
                    padding: '0.75rem', borderBottom: '1px solid var(--border)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
                  }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {r.first_name || r.last_name
                          ? `${r.first_name || ''} ${r.last_name || ''}`.trim()
                          : 'Story Report'}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {new Date(r.created_at).toLocaleDateString()} · {(r.amount_cents / 100).toFixed(2)} {r.currency?.toUpperCase()}
                      </div>
                    </div>
                    <div>
                      {r.status === 'ready' && r.pdf_s3_key
                        ? <button className="btn btn-primary btn-sm" onClick={() => downloadStoryReport(r.id)}>Download</button>
                        : <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                            {r.status === 'paid'       && 'Preparing…'}
                            {r.status === 'generating' && 'Generating PDF…'}
                            {r.status === 'pending'    && 'Awaiting payment'}
                            {r.status === 'failed'     && 'Failed'}
                            {r.status === 'refunded'   && 'Refunded'}
                          </span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      </div>
    </div>
  )
}
